import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  hasPassword: boolean;
  password?: string;
  createdAt: string;
}

const AdminUsersTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState<string>('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      } else {
        setError('Failed to load users');
      }
    } catch (error) {
      setError('Server connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionToggle = async (userId: number, currentRole: string) => {
    setActionLoading(userId);
    try {
      const newRole = currentRole === 'customer' ? 'lead' : 'customer';
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();
      if (data.success) {
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, role: data.user.role, hasPassword: data.user.hasPassword, password: data.user.password }
              : user
          )
        );
      } else {
        setError(data.message || 'Failed to change subscription');
      }
    } catch (error) {
      setError('Server connection error');
    } finally {
      setActionLoading(null);
    }
  };

  const copyPassword = async (password: string, userId: number) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopySuccess(`copied-${userId}`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="avp-loading">Loading...</div>;
  }

  return (
    <>
      <div className="avp-header">
        <div className="avp-stats">
          Total users: {users.length}
        </div>
      </div>

      {error && (
        <div className="avp-error-block">
          <h3 className="avp-error-title">Error</h3>
          <p className="avp-error-text">{error}</p>
        </div>
      )}

      <div className="avp-users-table">
        <div className="avp-table-header">
          <div className="avp-th">ID</div>
          <div className="avp-th">Name</div>
          <div className="avp-th">Email</div>
          <div className="avp-th">Role</div>
          <div className="avp-th">Password</div>
          <div className="avp-th">Created</div>
          <div className="avp-th">Actions</div>
        </div>
        
        {users.map((user) => (
          <div key={user.id} className="avp-table-row">
            <div className="avp-td" data-label="ID">{user.id}</div>
            <div className="avp-td avp-user-name" data-label="Name">{user.name}</div>
            <div className="avp-td avp-user-email" data-label="Email">{user.email}</div>
            <div className="avp-td" data-label="Role">
              <span className={`avp-role-badge avp-role-${user.role}`}>
                {user.role === 'customer' ? 'Customer' : 'Lead'}
              </span>
            </div>
            <div className="avp-td" data-label="Password">
              <div className="avp-password-cell">
                <span className={`avp-password-status ${user.hasPassword ? 'avp-has-password' : 'avp-no-password'}`}>
                  {user.hasPassword ? 'Yes' : 'No'}
                </span>
                {user.hasPassword && user.password && (
                  <button 
                    className="avp-copy-btn"
                    onClick={() => copyPassword(user.password!, user.id)}
                    title="Copy password"
                  >
                    {copySuccess === `copied-${user.id}` ? '✓' : '📋'}
                  </button>
                )}
              </div>
            </div>
            <div className="avp-td" data-label="Created">{formatDate(user.createdAt)}</div>
            <div className="avp-td" data-label="Actions">
              <button
                className={`avp-action-btn ${user.role === 'customer' ? 'avp-action-remove' : 'avp-action-add'}`}
                onClick={() => handleSubscriptionToggle(user.id, user.role)}
                disabled={actionLoading === user.id}
              >
                {actionLoading === user.id ? (
                  'Processing...'
                ) : user.role === 'customer' ? (
                  'Remove subscription'
                ) : (
                  'Add subscription'
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && !loading && (
        <div className="avp-no-videos">
          <p>No users found</p>
        </div>
      )}
    </>
  );
};

export default AdminUsersTab;
